import { useState } from "react";
import { useBulkReview } from "../api/recordings";
import { errorMessage } from "../api/client";
import ConfirmDialog from "./ConfirmDialog";
import type { BulkReviewResponse } from "../api/types";

interface Props {
  selectedIds: number[];
  /** All ids currently visible under the filters, for select-all. */
  visibleIds: number[];
  onSelectAll: () => void;
  onClear: () => void;
}

/** Batch review toolbar for the Recordings list: decide every selected recording in one
 * request. Mirrors ApproveBar's note + move-object options, so a batch decision is written
 * exactly like a per-recording one. */
export default function BulkReviewBar({
  selectedIds,
  visibleIds,
  onSelectAll,
  onClear,
}: Props) {
  const [note, setNote] = useState("");
  const [moveObject, setMoveObject] = useState(false);
  const [result, setResult] = useState<BulkReviewResponse | null>(null);
  const bulk = useBulkReview();

  const count = selectedIds.length;
  const allSelected = count > 0 && count === visibleIds.length;

  const [confirming, setConfirming] = useState<"approved" | "rejected" | null>(null);

  const run = (decision: "approved" | "rejected") => {
    setResult(null);
    bulk.mutate(
      {
        recording_ids: selectedIds,
        decision,
        note: note || null,
        move_object: moveObject,
      },
      {
        onSuccess: (res) => {
          setResult(res);
          setNote("");
          onClear();
        },
        onSettled: () => setConfirming(null),
      },
    );
  };

  const failures = (result?.results ?? []).filter((r) => r.error);

  return (
    <div className="bulk-bar">
      <div className="bulk-bar-row">
        <span className="bulk-count">
          {count} selected<span className="muted"> of {visibleIds.length}</span>
        </span>
        <button
          className="btn ghost small"
          disabled={visibleIds.length === 0 || allSelected}
          onClick={onSelectAll}
        >
          Select all
        </button>
        <button className="btn ghost small" disabled={count === 0} onClick={onClear}>
          Clear
        </button>

        <label className="move-toggle">
          <input
            type="checkbox"
            checked={moveObject}
            onChange={(e) => setMoveObject(e.target.checked)}
          />
          Move object
        </label>

        <input
          className="note-input"
          placeholder="Reviewer note (optional)…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <div className="action-buttons">
          <button
            className="btn reject"
            disabled={count === 0 || bulk.isPending}
            onClick={() => setConfirming("rejected")}
          >
            {bulk.isPending ? "Working…" : `Reject ${count || ""}`.trim()}
          </button>
          <button
            className="btn approve"
            disabled={count === 0 || bulk.isPending}
            onClick={() => setConfirming("approved")}
          >
            {bulk.isPending ? "Working…" : `Approve ${count || ""}`.trim()}
          </button>
        </div>
      </div>

      {bulk.isError && <p className="error">Batch review failed: {errorMessage(bulk.error)}</p>}
      {result && (
        <p className={failures.length ? "error" : "muted small"}>
          {result.updated} updated
          {failures.length > 0 &&
            ` · ${failures.length} skipped: ` +
              failures.map((f) => `#${f.recording_id} (${f.error})`).join(", ")}
        </p>
      )}
      <ConfirmDialog
        open={confirming !== null}
        title={`${confirming === "approved" ? "Approve" : "Reject"} ${count} recording${count === 1 ? "" : "s"}?`}
        confirmLabel={
          bulk.isPending
            ? "Working…"
            : confirming === "approved"
              ? `Approve ${count}`
              : `Reject ${count}`
        }
        tone={confirming === "rejected" ? "danger" : "primary"}
        busy={bulk.isPending}
        onConfirm={() => confirming && run(confirming)}
        onCancel={() => setConfirming(null)}
      >
        <p>The decision is written to every selected recording.</p>
      </ConfirmDialog>
    </div>
  );
}
